/**
 * Shared Add / Dismiss handling for Flux Agent custom-field suggestions, used by the in-chat
 * suggestion cards and the `suggest_custom_field` action card. Tracks per-insight progress and
 * reports the outcome with a toast; the server re-validates ownership and status.
 *
 * @module apps/web/src/components/flux-agent/learning/useApplySuggestion
 */

import { useCallback, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { fluxAgentLearningApi } from '@/lib/fluxAgentLearningApi';

/** Outcome of one suggestion as seen by this client. */
export type SuggestionOutcome = 'pending' | 'added' | 'dismissed';

/** State and actions returned by {@link useApplySuggestion}. */
export interface ApplySuggestionControls {
  /** Per-insight progress or final outcome; absent ids are untouched. */
  outcomes: Record<string, SuggestionOutcome>;
  /** Create the suggested field; resolves true when it was added (or already had been). */
  add: (id: string, name: string) => Promise<boolean>;
  /** Hide the suggestion; resolves true on success. */
  dismiss: (id: string) => Promise<boolean>;
}

/** Track Add / Dismiss calls for field suggestions and toast their results. */
export function useApplySuggestion(): ApplySuggestionControls {
  const { toast } = useToast();
  const [outcomes, setOutcomes] = useState<Record<string, SuggestionOutcome>>({});

  const set = (id: string, outcome: SuggestionOutcome | undefined) =>
    setOutcomes(current => {
      const next = { ...current };
      if (outcome) next[id] = outcome;
      else delete next[id];
      return next;
    });

  const add = useCallback(
    async (id: string, name: string) => {
      set(id, 'pending');
      const result = await fluxAgentLearningApi.applySuggestion(id);
      if (result.ok || result.code === 'FLUX_AGENT_INSIGHT_HANDLED') {
        set(id, 'added');
        if (result.ok)
          toast({ title: 'Field added', description: `“${name}” is now one of your fields.` });
        return true;
      }
      set(id, undefined);
      toast({
        title: 'Couldn’t add the field',
        description: result.message,
        variant: 'destructive',
      });
      return false;
    },
    [toast]
  );

  const dismiss = useCallback(
    async (id: string) => {
      set(id, 'pending');
      const result = await fluxAgentLearningApi.dismissInsight(id);
      if (result.ok || result.code === 'FLUX_AGENT_INSIGHT_HANDLED') {
        set(id, 'dismissed');
        return true;
      }
      set(id, undefined);
      toast({ title: 'Couldn’t dismiss it', description: result.message, variant: 'destructive' });
      return false;
    },
    [toast]
  );

  return { outcomes, add, dismiss };
}
